import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sales } from '@/lib/supabase/sales';
import { useAuth } from '@/hooks/useAuth';
import type {
  SalesOutlet, Company, CompanyLocation, Contact, CompanyOutletDistance,
  Signal, ResearchRun, KioskSite, KioskStatus,
} from './types';

const bff = (import.meta.env.VITE_BFF_BASE_URL as string) || '/api';

/** A company plus its embedded locations + per-outlet distances. */
export interface CompanyWithGeo extends Company {
  company_locations: CompanyLocation[];
  company_outlet_distance: CompanyOutletDistance[];
}

export function useSalesOutlets() {
  return useQuery({
    queryKey: ['sales', 'outlets'],
    queryFn: async (): Promise<SalesOutlet[]> => {
      const { data, error } = await sales
        .from('v_outlets')
        .select('*')
        .order('city', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as SalesOutlet[];
    },
  });
}

export function useCompanies() {
  return useQuery({
    queryKey: ['sales', 'companies'],
    queryFn: async (): Promise<CompanyWithGeo[]> => {
      const { data, error } = await sales
        .from('companies')
        .select('*, company_locations(*), company_outlet_distance(*)')
        .eq('is_archived', false)
        .order('score', { ascending: false, nullsFirst: false })
        .limit(2000);
      if (error) throw error;
      return (data ?? []) as CompanyWithGeo[];
    },
  });
}

export function useCompany(id: string | undefined) {
  return useQuery({
    enabled: !!id,
    queryKey: ['sales', 'company', id],
    queryFn: async () => {
      const [company, locations, contacts, distances, signals] = await Promise.all([
        sales.from('companies').select('*').eq('id', id!).single(),
        sales.from('company_locations').select('*').eq('company_id', id!),
        sales.from('contacts').select('*').eq('company_id', id!),
        sales.from('company_outlet_distance').select('*').eq('company_id', id!),
        sales.from('signals').select('*').eq('company_id', id!).order('observed_at', { ascending: false }).limit(50),
      ]);
      if (company.error) throw company.error;
      return {
        company: company.data as Company,
        locations: (locations.data ?? []) as CompanyLocation[],
        contacts: (contacts.data ?? []) as Contact[],
        distances: (distances.data ?? []) as CompanyOutletDistance[],
        signals: (signals.data ?? []) as Signal[],
      };
    },
  });
}

export function useResearchRuns(outletId?: string) {
  return useQuery({
    queryKey: ['sales', 'runs', outletId ?? 'all'],
    queryFn: async (): Promise<ResearchRun[]> => {
      let q = sales.from('research_runs').select('*').order('started_at', { ascending: false }).limit(25);
      if (outletId) q = q.eq('outlet_id', outletId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as ResearchRun[];
    },
  });
}

async function postBff(path: string, body: unknown) {
  const res = await fetch(`${bff}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.error || `request failed (${res.status})`);
  return json;
}

export function useRunResearch() {
  return useMutation({
    mutationFn: (body: {
      outlet_id?: string; city?: string; radius_km?: number;
      categories?: string[]; max_pages?: number; dry_run?: boolean;
    }) => postBff('/sales/research-run', body),
  });
}

// ── Kiosk sites (corporate parks / office hubs for kiosk installs) ────────────
export function useKioskSites(city?: string) {
  return useQuery({
    queryKey: ['sales', 'kiosk-sites', city ?? 'all'],
    queryFn: async (): Promise<KioskSite[]> => {
      let q = sales.from('kiosk_sites').select('*')
        .order('priority', { ascending: false })
        .order('user_ratings_total', { ascending: false, nullsFirst: false })
        .limit(1000);
      if (city) q = q.eq('city', city);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as KioskSite[];
    },
  });
}

export function useRunKioskSearch() {
  return useMutation({
    mutationFn: (body: { city: 'Mumbai' | 'Bengaluru'; localities?: string[]; queries?: string[]; max_pages?: number; dry_run?: boolean }) =>
      postBff('/sales/kiosk-search', body),
  });
}

export function useSetKioskStatus() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const actor = user ? { user_id: user.id, email: user.email } : undefined;
  return useMutation({
    mutationFn: (p: {
      id: string; status?: KioskStatus; notes?: string; assigned_name?: string;
      contact_name?: string; contact_email?: string; contact_phone?: string;
    }) => postBff('/sales/crm', { action: 'kiosk_set_status', actor, ...p }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales', 'kiosk-sites'] }),
  });
}

/** Park an intro email to a kiosk site's leasing contact in Gmail Drafts. */
export function useDraftKioskEmail() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const actor = user ? { user_id: user.id, email: user.email } : undefined;
  return useMutation({
    mutationFn: (p: { id: string; to_email?: string; subject?: string; body?: string; from_sender?: string }) =>
      postBff('/sales/crm', { action: 'kiosk_draft_email', actor, ...p }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales', 'kiosk-sites'] }),
  });
}

export function useRunWarmSignal() {
  return useMutation({
    mutationFn: (body: { scan_days?: number; warm_days?: number; geocode_limit?: number; match_m?: number }) =>
      postBff('/sales/warm-signal', body),
  });
}

/** Geocoding progress for the warm signal: how many delivery addresses remain
 *  ungeocoded (each Warm-signal run geocodes ~150, so this shows how many more
 *  runs are needed). Returns zeros gracefully if the rollup isn't readable. */
export function useWarmProgress() {
  return useQuery({
    queryKey: ['sales', 'warm-progress'],
    staleTime: 30_000,
    retry: 0,
    queryFn: async (): Promise<{ total: number; pending: number }> => {
      const [total, pending] = await Promise.all([
        sales.from('order_address_signal').select('addr_hash', { count: 'exact', head: true }),
        sales.from('order_address_signal').select('addr_hash', { count: 'exact', head: true }).eq('geocoded', false),
      ]);
      return { total: total.count ?? 0, pending: pending.count ?? 0 };
    },
  });
}
